import React from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { IndicatorSectionKey } from '../../ImpactIndicator/utils';
import {
  IndicatorDetails,
  IndicatorDetailsRoutes,
  SectionIndicators,
} from '../utils';
import { TrueImpactTooltip } from '../../ImpactSummary/QuestionTextTooltip';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { indicatorHeaderTooltip } from './IndicatorHeaderTooltip';
import { GuidanceTextResponse } from '../../utils';

type IndicatorStatus = 'completed' | 'inprogress' | 'unanswered';
export interface IndicatorHeaderSectionProps {
  selectedIndex: number;
  guidanceText: GuidanceTextResponse | undefined;
  isReportPassed: boolean;
  programName?: string;
  intermediaries?: string;
  serviceGoal?: string;
  serviceType?: string;
  route: IndicatorDetailsRoutes;
  indicators: SectionIndicators;
  selectedSectionKey: IndicatorSectionKey;
  handleSelectIndicator?: (
    sectionKey: IndicatorSectionKey,
    index: number,
    route?: IndicatorDetailsRoutes,
  ) => void;
}

const isIndicatorCompleted = (
  indicator: IndicatorDetails,
  isReportPassed: boolean,
): boolean => {
  return Boolean(
    (isReportPassed ? indicator.finalForecast : indicator.forecast) &&
      indicator.measurement,
  );
};

const isCurrentIndicatorSelected = (
  indicatorIndex: number,
  indicatorSectionKey: IndicatorSectionKey,
  selectedIndex: IndicatorHeaderSectionProps['selectedIndex'],
  selectedSectionKey: IndicatorHeaderSectionProps['selectedSectionKey'],
) =>
  indicatorSectionKey === selectedSectionKey &&
  indicatorIndex === selectedIndex;

const getIndicatorStatus = (
  indicator: IndicatorDetails,
  indicatorIndex: number,
  indicatorSectionKey: IndicatorSectionKey,
  selectedIndex: IndicatorHeaderSectionProps['selectedIndex'],
  selectedSectionKey: IndicatorHeaderSectionProps['selectedSectionKey'],
  isReportPassed: IndicatorHeaderSectionProps['isReportPassed'],
): IndicatorStatus => {
  if (
    isCurrentIndicatorSelected(
      indicatorIndex,
      indicatorSectionKey,
      selectedIndex,
      selectedSectionKey,
    )
  ) {
    return 'inprogress';
  }
  if (isIndicatorCompleted(indicator, isReportPassed)) return 'completed';
  return 'unanswered';
};

const StyledDiv = styled.div`
  .indicators-container {
    display: flex;
    width: 142px;
    gap: 2px;
    margin-bottom: ${SPACERS.tiny};
    & div {
      width: 100%;
    }
  }
`;

const getIndicatorCompletion = (indicator: IndicatorDetails): number => {
  let count = 0;
  if (indicator.forecast) count++;
  if (indicator.measurement) count++;
  return ((count || 0.5) / 3) * 100;
};

const inprogressStyles = (indicator: IndicatorDetails, selected: boolean) => `
  position: relative;
  overflow: hidden;
  & div {
    position: absolute;
    top: 0;
    left: 0;
    width: ${getIndicatorCompletion(indicator)}% !important;
    height: 8px;
    background-color: ${selected ? COLORS.ctaPrimary : COLORS.success};
    border-bottom-right-radius: 8px;
    border-top-right-radius: 8px;
  }
`;

const inprogressBarStyles = (
  status: IndicatorStatus,
  indicator: IndicatorDetails,
  selected: boolean,
) => (status === 'inprogress' ? inprogressStyles(indicator, selected) : '');

const StyledIndicatorBinder = styled.div<{
  indicator: IndicatorDetails;
  total: number;
  selected: boolean;
  borderRadiusLeft: boolean;
  borderRadiusRight: boolean;
  status: IndicatorStatus;
}>`
  cursor: pointer;
  height: 8px;
  ${({ borderRadiusLeft }) =>
    borderRadiusLeft
      ? 'border-bottom-left-radius: 8px; border-top-left-radius: 8px;'
      : ''}
  ${({ borderRadiusRight }) =>
    borderRadiusRight
      ? 'border-bottom-right-radius: 8px; border-top-right-radius: 8px;'
      : ''}
  ${({ status, indicator, selected }) =>
    inprogressBarStyles(status, indicator, selected)}
  background-color: ${({ status }) =>
    status === 'completed' ? COLORS.success : COLORS.grayLight};
`;

const IndicatorHeaderSection: React.FC<IndicatorHeaderSectionProps & {
  sectionName: string;
  sectionKey: IndicatorSectionKey;
}> = ({
  route,
  sectionKey,
  indicators,
  sectionName,
  programName,
  serviceGoal,
  serviceType,
  intermediaries,
  selectedIndex,
  isReportPassed,
  selectedSectionKey,
  handleSelectIndicator,
}) => {
  const indicatorList = indicators[sectionKey];
  const totalIndicators = indicatorList.length;

  if (totalIndicators === 0) return null;

  return (
    <StyledDiv>
      <div className="indicators-container">
        {indicatorList.map((x, i) => (
          <TrueImpactTooltip
            key={x.indicatorId}
            onClick={
              handleSelectIndicator
                ? () => handleSelectIndicator(sectionKey, i)
                : undefined
            }
            tooltipComponent={indicatorHeaderTooltip({
              route,
              indicator: x,
              programName,
              intermediaries,
              serviceGoal,
              serviceType,
              isCurrentIndicatorSelected: isCurrentIndicatorSelected(
                i,
                sectionKey,
                selectedIndex,
                selectedSectionKey,
              ),
            })}
          >
            <StyledIndicatorBinder
              indicator={x}
              selected={selectedIndex === i}
              status={
                selectedIndex === i &&
                route === 'completed' &&
                selectedSectionKey === sectionKey
                  ? 'completed'
                  : getIndicatorStatus(
                      x,
                      i,
                      sectionKey,
                      selectedIndex,
                      selectedSectionKey,
                      isReportPassed,
                    )
              }
              borderRadiusLeft={i === 0}
              borderRadiusRight={i === totalIndicators - 1}
              total={totalIndicators}
            >
              <div />
            </StyledIndicatorBinder>
          </TrueImpactTooltip>
        ))}
      </div>
      <TrueImpactText variant="body-small-bold">{sectionName}</TrueImpactText>
    </StyledDiv>
  );
};

export default IndicatorHeaderSection;
